import axios from 'axios'
import React, { useEffect, useMemo, useState } from 'react'

import useSWR from '../../../../hooks/useSWR'
import { swalChoose, swalError, swalSuccess } from '../../../../common/components/Swaler'
import { groupBy } from '../../../../common/utils'
import Companies from './Companies'
import Locks from './Locks'
import Roles from './Roles'
import useThrottledState from '../../../../hooks/useThrottledState'
import StorageSuppliers from './StorageSuppliers'

const Admins = () => {
  const [email, setEmail] = useThrottledState('')
  const [companySearch, setCompanySearch] = useState('')
  const [selectedLockIds, setSelectedLockIds] = useState([])

  const { data: admin, mutate: mutateAdmin } = useSWR(email.length > 1 && '/subject/admin/admins', { email }, { errorRetryCount: 0 })
  const { data: locks } = useSWR(admin && '/subject/admin/locks', { support: true, sort: true })
  const { data: companies } = useSWR(admin && '/subject/admin/companies')
  const { data: rentalCompanies } = useSWR(admin && '/subject/admin/rental_companies')

  const adminCompanyIds = useMemo(() => admin?.companies.map((c) => c._id), [admin])

  const locksToDisplay = useMemo(
    () =>
      companies &&
      rentalCompanies &&
      locks
        ?.filter((l) => !l.isCarrierDoor)
        .map((l) => {
          const companyName = l.company && companies.find((c) => c._id === l.company).name

          const rentalCompany = l.rental_company && rentalCompanies.find((c) => c._id === l.rental_company)

          return { ...l, companyName, rentalCompany: { name: rentalCompany?.name, company: rentalCompany?.company } }
        }),
    [locks, companies, rentalCompanies]
  )

  const sortedLocksToDisplay = useMemo(
    () =>
      locksToDisplay
        ?.sort((a, b) => (a.custom_name.toLowerCase() > b.custom_name.toLowerCase() ? 1 : -1))
        .sort((a, b) => {
          const aIsChecked = adminCompanyIds.includes(a.company) || adminCompanyIds.includes(a.rentalCompany?.company) || selectedLockIds.includes(a._id)
          const bIsChecked = adminCompanyIds.includes(b.company) || adminCompanyIds.includes(b.rentalCompany?.company) || selectedLockIds.includes(b._id)

          if (aIsChecked && !bIsChecked) {
            return -1
          }
          if (!aIsChecked && bIsChecked) {
            return 1
          }

          return 0
        }),
    [locksToDisplay, selectedLockIds]
  )

  const groupedCompanies = useMemo(() => groupBy(companies || [], (c) => c._id), [companies])
  const groupedRentalCompanies = useMemo(() => groupBy(rentalCompanies || [], (c) => c._id), [rentalCompanies])
  const searchedCompanies = useMemo(() => {
    if (!companySearch || companySearch.length < 2) return []

    return companies.filter((c) => c.name.toLowerCase().includes(companySearch.toLowerCase()))
  }, [companySearch, companies])

  const toggleLocks = (e) => {
    if (e.target.checked) setSelectedLockIds(selectedLockIds.concat(e.target.value))
    else setSelectedLockIds(selectedLockIds.filter((id) => id !== e.target.value))
  }

  const updateAdmin = async () => {
    if (admin.password && admin.password.length < 8) return swalError('Lösenordet måste ha minst 8 tecken')

    axios
      .put(`/subject/admin/admins/${admin._id}`, {
        password: admin.password,
        companies: admin.companies,
        roles: admin.roles,
        storageSuppliers: admin.storageSuppliers,
        locks: selectedLockIds.filter((id) => {
          const lock = locksToDisplay.find((l) => l._id === id)

          return !adminCompanyIds.includes(lock.company) && !adminCompanyIds.includes(lock.rentalCompany?.company)
        }),
      })
      .then(() => swalSuccess('Uppdaterat', '', 'OK'))
  }

  const deleteAdmin = async () =>
    swalChoose('Säker?', `Vill du verkligen radera ${admin.name}?`, ['Ja', 'Avbryt']).then((r) => {
      if (r.dismiss) return

      axios.delete(`/subject/admin/admins/${admin._id}`).then(() => {
        setEmail('')
        swalSuccess('Raderat!', '', 'OK')
      })
    })

  useEffect(() => {
    if (admin && locksToDisplay && companies) {
      const adminLockIds = admin.locks.map((l) => l._id)

      setSelectedLockIds(locksToDisplay.filter((l) => adminLockIds.includes(l._id)).map((l) => l._id))
    }
  }, [admin, locksToDisplay, companies])

  return (
    <div>
      <br />
      <input type="text" placeholder="Sök efter email" onChange={(e) => setEmail(e.target.value.trim())} />
      {admin && (
        <div style={{ width: '60%' }}>
          <p>{`Ändra ${admin.name || 'Okänd'}s uppgifter`}</p>
          <button className="qSaveButton" onClick={updateAdmin}>
            Uppdatera admin
          </button>
          <br />
          <br />
          <div>
            <input type="password" value={admin.password || ''} onChange={(e) => mutateAdmin({ ...admin, password: e.target.value })} placeholder="Nytt lösenord" />
          </div>
          <div>
            <h3>Roller</h3>
            <Roles admin={admin} mutateAdmin={mutateAdmin} />
          </div>
          <div>
            <h3>Följare på företag:</h3>
            <Companies
              companySearch={companySearch}
              setCompanySearch={setCompanySearch}
              searchedCompanies={searchedCompanies}
              admin={admin}
              mutateAdmin={mutateAdmin}
              groupedCompanies={groupedCompanies}
              groupedRentalCompanies={groupedRentalCompanies}
              selectedLockIds={selectedLockIds}
            />
            <hr />
            <h3>Leverantörer av produkter i förråd:</h3>
            <StorageSuppliers admin={admin} mutateAdmin={mutateAdmin} />
            <hr />
            <h3>Projekt</h3>
            <Locks locks={sortedLocksToDisplay} adminCompanyIds={adminCompanyIds} selectedLockIds={selectedLockIds} toggleLocks={toggleLocks} />
            <button className="qSaveButton" onClick={updateAdmin}>
              Uppdatera admin
            </button>
            <button style={{ background: 'red' }} onClick={deleteAdmin}>
              Radera admin
            </button>
            <br />
            <br />
          </div>
        </div>
      )}
    </div>
  )
}

export default Admins
